import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, CaretLeft, CaretRight, MagnifyingGlass, Receipt } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import { IEmpresaOption } from '../../../../../hooks/useContaCilImportar';
import {
  IExtratoItem,
  baixarExtrato,
  useContaCilExtratos,
  useContaCilExtratosUsuarios,
  useExcluirExtrato,
  useReprocessarExtrato,
} from '../../../../../hooks/useContaCilExtratos';
import { useContaCilRegrasBancos } from '../../../../../hooks/useContaCilRegras';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { propsMenuSelect } from '../../../../../utils/selectMenu';
import {
  PeriodoRange,
  buildTodosPeriodo,
} from '../../../../analiseBots/components/PeriodoFlowFilter';
import { PeriodoSegment } from '../components/PeriodoSegment';
import { EmpresaPicker } from '../components/EmpresaPicker';
import { useAplicarEmpresaParam } from '../useEmpresaParam';
import { usePosicionarNoExtratoParam } from '../useExtratoParam';
import { useVoltar } from '../useVoltar';
import { ExtratoCard } from './components/ExtratoCard';
import { ExtratoDetailModal } from './components/ExtratoDetailModal';
import { ExtratoKpis, FiltroStatus, KPIS_COM_SUBFILTRO } from './components/ExtratoKpis';

const PAGE_SIZE = 24;

export function ConciliacaoContabilExtratosPage() {
  const voltar = useVoltar();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [empresa, setEmpresa] = useState<IEmpresaOption | null>(null);
  // Aplica `?empresa=<id>` dos atalhos da pagina Empresas (uma vez, no mount).
  useAplicarEmpresaParam(setEmpresa);
  const [status, setStatus] = useState<FiltroStatus>('');
  // Banco: select por banco_nome (mesma fonte do filtro de Regras — bancos
  // em uso). Texto livre filtrava banco_nome (NULL pra extratos nao-concluidos)
  // e escondia tudo silenciosamente.
  const [bancoNome, setBancoNome] = useState<string | null>(null);
  const [ordem, setOrdem] = useState<'recentes' | 'antigos'>('antigos');
  const [searchInput, setSearchInput] = useState('');
  const [searchAplicado, setSearchAplicado] = useState('');
  const [page, setPage] = useState(1);
  // Default = todos os períodos (sem filtro de data => backend devolve tudo).
  const [periodo, setPeriodo] = useState<PeriodoRange>(() => buildTodosPeriodo());
  // Filtro por quem importou (picker) + por origem do upload (toggle).
  const [usuario, setUsuario] = useState<{ value: number; label: string } | null>(null);
  const [origem, setOrigem] = useState<string | null>(null);
  // Segundo nível de filtro dos cards que agregam mais de um status
  // ("A identificar" e "Com erro"). null = todos os status do card.
  const [subStatus, setSubStatus] = useState<string | null>(null);

  const [selecionado, setSelecionado] = useState<IExtratoItem | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  // `?extrato=<id>` — deep-link do card da tela de Importar. Não abre o modal:
  // posiciona a LISTA no card, com os filtros que o alcançam, pra o operador
  // ver onde o documento está. Ver useExtratoParam.
  const posicionar = useCallback((p: { empresa: IEmpresaOption | null; kpi: string }) => {
    if (p.empresa) setEmpresa(p.empresa);
    setStatus(p.kpi as FiltroStatus);
    setOrdem('recentes');
    setPage(1);
  }, []);
  const extratoDestacado = usePosicionarNoExtratoParam(posicionar);
  const [paraExcluir, setParaExcluir] = useState<IExtratoItem | null>(null);
  const [baixandoId, setBaixandoId] = useState<number | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Debounce so do search (400ms). Banco/periodo sao instantaneos.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchAplicado(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: bancos = [] } = useContaCilRegrasBancos({
    empresaId: empresa?.value,
    emUso: true,
  });
  const bancoOptions = useMemo(
    () => [
      { value: '', label: 'Todos os bancos' },
      ...bancos.map(b => ({ value: b.nome, label: b.nome })),
    ],
    [bancos],
  );

  const { data: usuariosFacet = [] } = useContaCilExtratosUsuarios();
  const usuarioOptions = useMemo(
    () => [
      { value: 0, label: 'Todos os usuários' },
      ...usuariosFacet
        .filter(u => u.usuario_id != null)
        // Nome primeiro: e-mail sozinho não identifica a pessoa pra área. Cai no
        // e-mail quando o usuário foi removido do cadastro.
        .map(u => ({ value: u.usuario_id as number, label: u.nome || u.email })),
    ],
    [usuariosFacet],
  );

  // Card agregador ativo + sub-status escolhido => manda só aquele. Sem
  // escolha, manda a lista inteira do card.
  const kpiAtivo = KPIS_COM_SUBFILTRO.find(k => k.value === status);
  const statusEfetivo = kpiAtivo && subStatus ? subStatus : status;

  const { data, isFetching } = useContaCilExtratos({
    empresaId: empresa?.value,
    status: statusEfetivo || undefined,
    banco: bancoNome || undefined,
    search: searchAplicado || undefined,
    ordem,
    dateIni: periodo.date_init || undefined,
    dateFim: periodo.date_end || undefined,
    usuarioId: usuario?.value,
    origem: origem || undefined,
    page,
    size: PAGE_SIZE,
  });

  const itens = data?.items ?? [];
  const totalPages = data?.pages ?? 1;
  const resumo = data?.resumo;

  // Drawer/modal sincronizado com a lista viva (status/saldos mudam após
  // reprocessar ou polling) — usa a linha viva, com fallback ao snapshot.
  const modalExtrato = selecionado
    ? (itens.find(i => i.id === selecionado.id) ?? selecionado)
    : null;

  const temFiltros = Boolean(
    empresa ||
      status ||
      bancoNome ||
      searchAplicado ||
      periodo.preset !== 'todos' ||
      usuario ||
      origem,
  );

  const reprocessar = useReprocessarExtrato();
  const excluir = useExcluirExtrato();
  // Card em exclusão. Vive aqui (e não em `excluir.isPending`) porque precisa
  // durar até o REFETCH da lista, não só até a resposta do DELETE.
  const [excluindoId, setExcluindoId] = useState<number | null>(null);

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ['contacil', 'extratos'] });

  const selecionarStatus = (value: FiltroStatus) => {
    // Trocar de card descarta o sub-filtro, senão ele ficaria aplicado
    // invisível no card seguinte.
    if (value !== status) setSubStatus(null);
    // Clicar no KPI ativo (≠ Total) volta pra Todos.
    setStatus(prev => (prev === value && value !== '' ? '' : value));
    setPage(1);
  };

  const limparFiltros = () => {
    setEmpresa(null);
    setStatus('');
    setBancoNome(null);
    setSearchInput('');
    setSearchAplicado('');
    setOrdem('antigos');
    setPeriodo(buildTodosPeriodo());
    setUsuario(null);
    setOrigem(null);
    setSubStatus(null);
    setPage(1);
  };

  const abrirModal = (item: IExtratoItem) => {
    setSelecionado(item);
    setModalAberto(true);
  };

  const onBaixar = async (item: IExtratoItem) => {
    if (baixandoId != null) return;
    setBaixandoId(item.id);
    try {
      const { url } = await baixarExtrato(item.id);
      // Content-Disposition: attachment força o download; âncora evita o
      // bloqueio de popup que window.open sofre após o await.
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast({ status: 'error', title: extractApiErrorMessage(err, 'Falha ao baixar o arquivo.') });
    } finally {
      setBaixandoId(null);
    }
  };

  const onReprocessar = async (item: IExtratoItem) => {
    try {
      await reprocessar.mutateAsync(item.id);
      toast({ status: 'success', title: 'Reprocessamento enfileirado.' });
      recarregar();
    } catch (err) {
      toast({ status: 'error', title: extractApiErrorMessage(err, 'Falha ao reprocessar.') });
    }
  };

  const confirmarExclusao = async () => {
    if (!paraExcluir) return;
    const { id } = paraExcluir;
    // Fecha o diálogo já: quem dá o retorno visual agora é o próprio card.
    setParaExcluir(null);
    setExcluindoId(id);
    try {
      const res = await excluir.mutateAsync(id);
      toast({
        status: 'success',
        title: 'Extrato excluído.',
        description: `${res.lancamentos_afetados} lançamento(s) removido(s).`,
      });
      if (selecionado?.id === id) setModalAberto(false);
      // AGUARDA o refetch, não só a resposta do DELETE. Sem isso o card some
      // antes de a lista confirmar a exclusão, e quem reimporta em seguida
      // esbarra no "já importado" achando que a exclusão falhou.
      await queryClient.invalidateQueries({ queryKey: ['contacil', 'extratos'] });
    } catch (err) {
      toast({ status: 'error', title: extractApiErrorMessage(err, 'Falha ao excluir.') });
    } finally {
      setExcluindoId(null);
    }
  };

  const carregandoInicial = isFetching && itens.length === 0;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={6}>
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color="#64748B"
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
              onClick={voltar}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="64px"
              h="64px"
              borderRadius="2xl"
              bgGradient="linear(135deg, #E0E7FF 0%, #DDD6FE 100%)"
              color="#6366F1"
              boxShadow="0 8px 24px -6px rgba(99, 102, 241, 0.4)"
              flexShrink={0}
            >
              <Receipt size={32} weight="duotone" />
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Conciliação Contábil
              </Text>
              <Heading fontSize={{ base: '22px', md: '28px' }} fontWeight="700" color="#0F172A">
                Extratos enviados
              </Heading>
            </Box>
          </Flex>
        </motion.div>

        {/* KPIs (clicáveis = filtro de status) */}
        <ExtratoKpis
          resumo={resumo}
          statusAtivo={status}
          onSelect={selecionarStatus}
          isLoading={carregandoInicial}
        />

        {/* Filtros */}
        <GlassCard intensity="medium" p={4} mb={5}>
          <Flex gap={3} wrap="wrap" align={{ md: 'flex-end' }}>
            <Box flex={1} minW="240px">
              <EmpresaPicker
                value={empresa}
                onChange={opt => {
                  setEmpresa(opt);
                  setPage(1);
                }}
                placeholderOverride="Todas — digite p/ filtrar"
              />
            </Box>
            <Box w={{ base: 'full', md: '180px' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Banco
              </Text>
              <RSelect
                size="sm"
                options={bancoOptions}
                value={bancoOptions.find(o => o.value === (bancoNome || '')) ?? bancoOptions[0]}
                onChange={opt => {
                  setBancoNome((opt as { value: string })?.value || null);
                  setPage(1);
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                {...propsMenuSelect}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            </Box>
            <Box flex={1} minW="200px">
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Buscar
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <MagnifyingGlass size={14} color="#94A3B8" />
                </InputLeftElement>
                <Input
                  borderRadius="lg"
                  bg="white"
                  placeholder="Arquivo, titular ou CNPJ"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
              </InputGroup>
            </Box>
            <Box w={{ base: 'full', md: 'auto' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Período
              </Text>
              <PeriodoSegment
                value={periodo}
                onChange={r => {
                  setPeriodo(r);
                  setPage(1);
                }}
              />
            </Box>
            <Box w={{ base: 'full', md: '200px' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Importado por
              </Text>
              <RSelect
                size="sm"
                options={usuarioOptions}
                value={
                  usuarioOptions.find(o => o.value === (usuario?.value ?? 0)) ?? usuarioOptions[0]
                }
                onChange={opt => {
                  const v = opt as { value: number; label: string };
                  setUsuario(v && v.value ? { value: v.value, label: v.label } : null);
                  setPage(1);
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
                {...propsMenuSelect}
                chakraStyles={{ control: provided => ({ ...provided, borderRadius: 'lg' }) }}
              />
            </Box>
            <Box w={{ base: 'full', md: 'auto' }}>
              <Text
                fontSize="10px"
                fontWeight="600"
                color="#64748B"
                mb={1}
                textTransform="uppercase"
              >
                Origem
              </Text>
              <Flex align="center" gap="2px" bg="rgba(15,23,42,0.04)" borderRadius="full" p="3px">
                {(
                  [
                    { v: null, label: 'Todas' },
                    { v: 'escritorio', label: 'Escritório' },
                    { v: 'cliente', label: 'Cliente' },
                  ] as const
                ).map(o => (
                  <Button
                    key={o.label}
                    size="xs"
                    h="26px"
                    px={3}
                    borderRadius="full"
                    variant="unstyled"
                    fontWeight="600"
                    fontSize="12px"
                    bg={origem === o.v ? 'white' : 'transparent'}
                    color={origem === o.v ? '#6366F1' : '#64748B'}
                    boxShadow={origem === o.v ? '0 1px 4px -1px rgba(15,23,42,0.15)' : undefined}
                    onClick={() => {
                      setOrigem(o.v);
                      setPage(1);
                    }}
                  >
                    {o.label}
                  </Button>
                ))}
              </Flex>
            </Box>
            {kpiAtivo?.subFiltros && (
              <Box w={{ base: 'full', md: 'auto' }}>
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color="#64748B"
                  mb={1}
                  textTransform="uppercase"
                >
                  Detalhar
                </Text>
                <Flex align="center" gap="2px" bg="rgba(15,23,42,0.04)" borderRadius="full" p="3px">
                  {[{ value: null as string | null, label: 'Todos' }, ...kpiAtivo.subFiltros].map(
                    t => (
                      <Button
                        key={t.label}
                        size="xs"
                        h="26px"
                        px={3}
                        borderRadius="full"
                        variant="unstyled"
                        fontWeight="600"
                        fontSize="12px"
                        bg={subStatus === t.value ? 'white' : 'transparent'}
                        color={subStatus === t.value ? '#6366F1' : '#64748B'}
                        boxShadow={
                          subStatus === t.value ? '0 1px 4px -1px rgba(15,23,42,0.15)' : undefined
                        }
                        onClick={() => {
                          setSubStatus(t.value);
                          setPage(1);
                        }}
                      >
                        {t.label}
                      </Button>
                    ),
                  )}
                </Flex>
              </Box>
            )}
          </Flex>
        </GlassCard>

        {/* Cabeçalho da lista */}
        <Flex align="center" justify="space-between" gap={3} mb={3} px={1} wrap="wrap">
          <Text fontSize="sm" fontWeight="600" color="#0F172A">
            {data?.total ?? 0} extrato(s)
            {isFetching && itens.length > 0 && (
              <Spinner size="xs" ml={2} color="#1B88F5" thickness="2px" />
            )}
          </Text>
          <Flex align="center" gap={2}>
            <Flex align="center" gap="2px" bg="rgba(15,23,42,0.04)" borderRadius="full" p="3px">
              {(['recentes', 'antigos'] as const).map(o => (
                <Button
                  key={o}
                  size="xs"
                  h="26px"
                  px={3}
                  borderRadius="full"
                  variant="unstyled"
                  fontWeight="600"
                  fontSize="12px"
                  bg={ordem === o ? 'white' : 'transparent'}
                  color={ordem === o ? '#6366F1' : '#64748B'}
                  boxShadow={ordem === o ? '0 1px 4px -1px rgba(15,23,42,0.15)' : undefined}
                  onClick={() => {
                    setOrdem(o);
                    setPage(1);
                  }}
                >
                  {o === 'recentes' ? 'Recentes' : 'Antigos'}
                </Button>
              ))}
            </Flex>
            {temFiltros && (
              <Button
                size="xs"
                variant="ghost"
                borderRadius="full"
                color="#64748B"
                onClick={limparFiltros}
              >
                Limpar filtros
              </Button>
            )}
          </Flex>
        </Flex>

        {/* Grid de cards — loading / vazio / lista */}
        {carregandoInicial && (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {Array.from({ length: 6 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <GlassCard key={i} intensity="medium" p={4}>
                <Skeleton height="16px" width="70%" />
                <Skeleton height="12px" width="40%" mt={2} />
                <SkeletonText mt={4} noOfLines={4} spacing={3} />
                <Skeleton height="28px" mt={4} borderRadius="full" />
              </GlassCard>
            ))}
          </SimpleGrid>
        )}

        {!carregandoInicial && itens.length === 0 && (
          <GlassCard intensity="medium" py={16}>
            <Flex direction="column" align="center" gap={3}>
              <Receipt size={40} weight="duotone" color="#CBD5E1" />
              <Text fontSize="sm" color="#94A3B8" textAlign="center">
                {temFiltros
                  ? 'Nenhum extrato encontrado para os filtros atuais.'
                  : 'Nenhum extrato importado ainda.'}
              </Text>
              {temFiltros && (
                <Button size="sm" variant="outline" borderRadius="lg" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              )}
            </Flex>
          </GlassCard>
        )}

        {!carregandoInicial && itens.length > 0 && (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {itens.map((e, idx) => (
              <ExtratoCard
                key={e.id}
                extrato={e}
                index={idx}
                onOpen={abrirModal}
                onBaixar={onBaixar}
                onReprocessar={onReprocessar}
                onExcluir={item => setParaExcluir(item)}
                baixando={baixandoId === e.id}
                reprocessando={reprocessar.isPending && reprocessar.variables === e.id}
                excluindo={excluindoId === e.id}
                destacado={e.id === extratoDestacado}
              />
            ))}
          </SimpleGrid>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <Flex align="center" justify="center" gap={3} mt={6}>
            <IconButton
              aria-label="Página anterior"
              icon={<CaretLeft size={14} weight="bold" />}
              size="xs"
              variant="ghost"
              borderRadius="full"
              isDisabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            />
            <Text fontSize="xs" color="#64748B" fontWeight="600">
              {page} / {totalPages}
            </Text>
            <IconButton
              aria-label="Próxima página"
              icon={<CaretRight size={14} weight="bold" />}
              size="xs"
              variant="ghost"
              borderRadius="full"
              isDisabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            />
          </Flex>
        )}
      </Box>

      <ExtratoDetailModal
        extrato={modalExtrato}
        isOpen={modalAberto}
        onClose={() => {
          setModalAberto(false);
          setSelecionado(null);
        }}
        onBaixar={onBaixar}
        onReprocessar={onReprocessar}
        onExcluir={item => setParaExcluir(item)}
        reprocessando={reprocessar.isPending}
      />

      <AlertDialog
        isOpen={!!paraExcluir}
        leastDestructiveRef={cancelRef}
        onClose={() => setParaExcluir(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Excluir extrato
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm" color="#475569">
              Excluir o extrato <b>{paraExcluir?.nome_arquivo}</b>? Os lançamentos gerados por ele
              também serão removidos (somem da tela Conciliar). A ação é reversível no banco.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button
                ref={cancelRef}
                size="sm"
                variant="ghost"
                onClick={() => setParaExcluir(null)}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                colorScheme="red"
                borderRadius="lg"
                isLoading={excluir.isPending}
                onClick={confirmarExclusao}
              >
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </GlassPageBg>
  );
}
